import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ClipboardCheck, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const MODULE_BADGE = { expense: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400",
  purchase_order: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-400",
  payment: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400" };

export default function Approvals() {
  const { activeId } = useCompany();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [decision, setDecision] = useState({ open: false, req: null, action: "approve", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/approvals?status=${tab === "pending" ? "pending" : "all"}&limit=200`)
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId, tab]); // eslint-disable-line

  const decide = async () => {
    setBusy(true);
    try {
      await api.post(`/approvals/${decision.req.id}/decide`, { action: decision.action, note: decision.note });
      toast.success(`Request ${decision.action}d`);
      setDecision({ open: false, req: null, action: "approve", note: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const items = data?.items.filter((r) => tab === "pending" ? r.status === "pending" : r.status !== "pending") || [];

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" testId="approvals-title"
        sub={data ? `${data.pending_count} pending decision${data.pending_count === 1 ? "" : "s"}` : "Purchase orders, expenses and payments awaiting sign-off"} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pending" data-testid="approvals-tab-pending"><ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Pending</TabsTrigger>
          <TabsTrigger value="history" data-testid="approvals-tab-history">History</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : items.length === 0 ? (
          <EmptyState testId="approvals-empty" title={tab === "pending" ? "Nothing pending" : "No history yet"}
            body={tab === "pending" ? "Submitted purchase orders and expenses appear here for approval." : "Decided requests appear here."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="approvals-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Module</th><th className="p-3">Reference</th><th className="p-3">Title</th>
                <th className="p-3 text-right">Amount</th><th className="p-3">Requested By</th><th className="p-3">Date</th>
                {tab === "history" && <th className="p-3">Decision</th>}
                {tab === "pending" && data.can_decide && <th className="p-3"></th>}</tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`approval-row-${r.record_no || r.id}`}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3"><Badge className={`${MODULE_BADGE[r.module] || ""} border-0 text-[10px]`}>{r.module.replace(/_/g, " ")}</Badge></td>
                    <td className="p-3 font-mono text-xs font-semibold">{r.record_no}</td>
                    <td className="p-3 max-w-[220px] truncate">{r.title}</td>
                    <td className="p-3 text-right"><Money value={r.amount} className="font-semibold" /></td>
                    <td className="p-3 text-slate-500">{r.requested_by_name}</td>
                    <td className="p-3 text-slate-500">{fmtDate(r.created_at)}</td>
                    {tab === "history" && (
                      <td className="p-3">
                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${
                          r.status === "approved"
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400"
                            : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400"}`}>
                          {r.status}
                        </span>
                        <span className="ml-1.5 text-xs text-slate-400">{r.decided_by}</span>
                      </td>
                    )}
                    {tab === "pending" && data.can_decide && (
                      <td className="p-3">
                        <div className="flex gap-1.5 justify-end">
                          <Button size="sm" data-testid={`approve-${r.id}`}
                            className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => setDecision({ open: true, req: r, action: "approve", note: "" })}>
                            <Check className="mr-1 h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`reject-${r.id}`}
                            className="h-7 text-red-600 border-red-200 dark:border-red-900"
                            onClick={() => setDecision({ open: true, req: r, action: "reject", note: "" })}>
                            <X className="mr-1 h-3.5 w-3.5" /> Reject
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={decision.open} onOpenChange={() => setDecision({ open: false, req: null, action: "approve", note: "" })}>
        <DialogContent aria-describedby={undefined} className="max-w-md bg-white dark:bg-slate-900" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {decision.action === "approve" ? "Approve" : "Reject"} {decision.req?.record_no}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {decision.req?.title} · <Money value={decision.req?.amount || 0} className="font-semibold" />
            {decision.action === "approve"
              ? decision.req?.module === "purchase_order" ? " — the PO becomes Approved and can be converted to a bill." : " — the expense is posted to books."
              : decision.req?.module === "purchase_order" ? " — the PO returns to Draft." : " — the expense is marked Rejected."}
          </p>
          <div>
            <Input data-testid="decision-note" placeholder="Note (optional)" value={decision.note}
              onChange={(e) => setDecision((d) => ({ ...d, note: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision({ open: false, req: null, action: "approve", note: "" })}>Back</Button>
            <Button data-testid="decision-confirm-btn" onClick={decide} disabled={busy}
              className={decision.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}>
              {busy ? "Working..." : `Confirm ${decision.action}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
